import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Loader2, Eye, EyeOff, Lock, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useLanguage } from '@/store/language-store'
import { useTheme } from '@/components/ThemeProvider'
import { cn } from '@/lib/utils'
import LogoDark from '../assets/logo-dark.svg?url'
import LogoLight from '../assets/logo-light.svg?url'

export default function ResetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { language } = useLanguage()
  const { theme } = useTheme()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [resetFailed, setResetFailed] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [countdown, setCountdown] = useState(3)

  // Get error parameters and hash from the URL
  const errorParam = searchParams.get('error')
  const errorCode = searchParams.get('error_code')
  const errorDescription = searchParams.get('error_description')
  const hash = window.location.hash

  useEffect(() => {
    // Check for error parameters in the URL
    if (errorParam === 'access_denied' && errorCode === 'otp_expired') {
      setResetFailed(true)
      setError(errorDescription || t('auth.passwordResetLinkExpired'))
    }
    
    // If there's a hash in the URL, try to get the access token
    if (hash && hash.includes('access_token=')) {
      // The hash is already being handled by Supabase's internal auth mechanism
      console.log('Reset password hash detected')
    }
  }, [errorParam, errorCode, errorDescription, hash, t])

  // Countdown effect for redirection
  useEffect(() => {
    if (resetSuccess) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            // Force navigation to login page
            window.location.href = '/auth/login'
            return 0
          }
          return prev - 1
        })
      }, 1000)
      
      return () => clearInterval(timer)
    }
  }, [resetSuccess])

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Reset error
    setError('')
    
    // Validate inputs
    if (!password || !confirmPassword) {
      setError(t('auth.errors.fillAllFields'))
      return
    }
    
    if (password !== confirmPassword) {
      setError(t('auth.errors.passwordMismatch'))
      return
    }
    
    // Check password strength based on Supabase requirements
    const hasMinLength = password.length >= 8
    const hasNumber = /[0-9]/.test(password)
    const hasLowercase = /[a-z]/.test(password)
    const hasUppercase = /[A-Z]/.test(password)
    const hasSpecial = /[^A-Za-z0-9]/.test(password)
    
    // Validate password against Supabase requirements
    if (!hasMinLength) {
      setError(t('auth.password.requirements.minLength'))
      return
    }
    
    // Check if password contains all required character types
    const missingRequirements = []
    
    if (!hasLowercase) missingRequirements.push(t('auth.password.requirements.lowercase'))
    if (!hasUppercase) missingRequirements.push(t('auth.password.requirements.uppercase'))
    if (!hasNumber) missingRequirements.push(t('auth.password.requirements.number'))
    if (!hasSpecial) missingRequirements.push(t('auth.password.requirements.special'))
    
    if (missingRequirements.length > 0) {
      setError(t('auth.errors.passwordRequirements') + ': ' + missingRequirements.join(', '))
      return
    }
    
    setIsLoading(true)
    
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password
      })
      
      if (error) {
        throw error
      }
      
      // Show success message
      toast.success(t('auth.passwordResetSuccess'), {
        description: t('auth.passwordResetSuccessDescription'),
        duration: 5000 // Show for 5 seconds to ensure visibility
      })
      
      // Set success state
      setResetSuccess(true)
      
    } catch (error: any) {
      console.error('Password reset error:', error)
      
      if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
        if (error.message.includes('too many requests')) {
          setError(t('auth.tooManyRequests'))
        } else if (error.message.includes('network')) {
          setError(t('auth.networkError'))
        } else if (error.message.includes('Auth session missing')) {
          setError(t('auth.errors.sessionExpired'))
          setResetFailed(true)
        } else {
          setError(error.message || t('auth.passwordResetError'))
        }
        
        toast.error(t('auth.passwordResetError'), {
          description: error.message
        })
      } else {
        setError(t('auth.passwordResetError'))
        toast.error(t('auth.passwordResetError'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  const requestNewResetLink = () => {
    navigate('/auth/forgot-password')
  }

  return (
    <div className={cn(
      "min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-background-primary dark:bg-background-dark-primary",
      language === 'ar' ? 'font-arabic' : 'font-sans'
    )}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="p-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
            <img 
              src={theme === 'dark' ? LogoDark : LogoLight}
              alt="Flows.Cash Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
          {resetSuccess 
            ? t('auth.passwordResetSuccess') 
            : resetFailed 
              ? t('auth.passwordResetFailed') 
              : t('auth.setNewPassword')
          }
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary dark:text-text-dark-secondary">
          {resetSuccess 
            ? t('auth.passwordResetSuccessDescription') 
            : resetFailed 
              ? t('auth.passwordResetFailedDescription') 
              : t('auth.setNewPasswordDescription')
          }
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 py-8 px-4 shadow-sm sm:rounded-xl sm:px-10">
          {error && !resetFailed && !resetSuccess && (
            <div className="mb-6 p-3 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          
          {resetSuccess ? (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20">
                  <CheckCircle className="h-12 w-12 text-green-500 dark:text-green-400" />
                </div>
              </div>
              
              <p className="text-center text-green-600 dark:text-green-400 font-medium">
                {t('auth.passwordResetSuccessDescription')}
              </p>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {t('auth.redirectingToLogin', { seconds: countdown })}
                </p>
              </div>
              
              <Link
                to="/auth/login"
                className="w-full flex items-center justify-center px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:bg-primary-dark dark:hover:bg-primary-dark/90 dark:focus:ring-primary-dark transition-colors"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          ) : resetFailed ? (
            <div className="text-center space-y-6">
              <div className="p-3 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{t('auth.passwordResetFailed')}</p>
              </div>
              
              <p className="text-center text-red-500 dark:text-red-400">
                {error || t('auth.passwordResetLinkExpired')}
              </p>
              
              <div className="flex flex-col space-y-4">
                <button
                  onClick={requestNewResetLink}
                  className="w-full flex items-center justify-center px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:bg-primary-dark dark:hover:bg-primary-dark/90 dark:focus:ring-primary-dark transition-colors"
                >
                  {t('auth.requestNewResetLink')}
                </button>
                
                <Link
                  to="/auth/login"
                  className="w-full flex items-center justify-center px-4 py-2.5 bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary dark:focus:ring-primary-dark transition-colors"
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleResetPassword}>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {t('auth.password.label')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.password.placeholder')}
                    className="w-full pl-3 pr-10 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="mt-1">
                  <ul className="text-xs text-text-secondary dark:text-text-dark-secondary space-y-1">
                    <li className={cn(
                      password.length >= 8 ? "text-green-500 dark:text-green-400" : ""
                    )}>
                      {t('auth.password.requirements.minLength')}
                    </li>
                    <li className={cn(
                      /[0-9]/.test(password) ? "text-green-500 dark:text-green-400" : ""
                    )}>
                      {t('auth.password.requirements.number')}
                    </li>
                    <li className={cn(
                      /[a-z]/.test(password) ? "text-green-500 dark:text-green-400" : ""
                    )}>
                      {t('auth.password.requirements.lowercase')}
                    </li>
                    <li className={cn(
                      /[A-Z]/.test(password) ? "text-green-500 dark:text-green-400" : ""
                    )}>
                      {t('auth.password.requirements.uppercase')}
                    </li>
                    <li className={cn(
                      /[^A-Za-z0-9]/.test(password) ? "text-green-500 dark:text-green-400" : ""
                    )}>
                      {t('auth.password.requirements.special')}
                    </li>
                  </ul>
                  <p className="mt-2 text-xs text-text-secondary dark:text-text-dark-secondary">
                    {t('auth.password.requirements.note')}
                  </p>
                </div>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {t('auth.verifyPassword.label')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.verifyPassword.placeholder')}
                    className="w-full pl-3 pr-10 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:bg-primary-dark dark:hover:bg-primary-dark/90 dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('auth.updatingPassword')}
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      {t('auth.updatePassword')}
                    </>
                  )}
                </button>
              </div>
              
              <div className="mt-4 text-center">
                <Link
                  to="/auth/login"
                  className="text-sm text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 hover:underline focus:outline-none transition-colors"
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
} 